import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment";

import Modal, { ModalBody } from "Components/Modal";
import InputSearch from "Components/InputSearch";
import { Grid, LinearProgress, Badge, Typography, Divider } from "@mui/material";
import { Button } from "Components/uiControls";
import { Add } from "@mui/icons-material";

import ChatsList from "../chatsList";
import TeamChatsList from "../chatTypes/team/components/TeamChatsList";
import TeamMemberSelector from "../chatTypes/team/components/TeamMemberSelector";

import {
  openChatList,
  fetchChats,
  chatListContactsOpen,
  closeSupportChat,
  openChatThread,
} from "Modules/chat/.globals/redux/actions";

import { reset as resetChatList, close as closeChatList, setIsExpanded } from "Modules/chat/views/chatsContainer/redux/slice";
import { close as closeTeamMemberSelector } from "Modules/chat/views/chatTypes/team/redux/contactsSlice";
import { close as closeTeamChatList } from "Modules/chat/views/chatTypes/team/redux/listSlice";
import { resetChatThread } from "Modules/chat/.globals/redux/actions";
import { selectAIUnreadCount, fetchAIUnreadCounts, incrementUnreadCount } from "Modules/chat/views/chatTypes/ai/redux/aiChatListSlice";
import { openChat } from "Modules/chat/views/chatTypes/support/redux/supportChannelsSlice";
import AINotification from "Modules/chat/views/chatTypes/ai/components/AINotification";

import "Modules/chat/index.scss";

const ChatsContainer = () => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [aiNotif, setAiNotif] = useState(null);
  const typingTimeoutRef = useRef(null);

  // Combine related selectors for better performance
  const { authId, chatUsers } = useSelector((state) => ({
    authId: state.securityRedux.authId,
    chatUsers: state.catalogsRedux.chatUsers,
  }), shallowEqual);

  const {
    openModal,
    loading,
    chats,
    total,
    isExpanded,
    isChatsLoading,
  } = useSelector((state) => state.chatsContainerRedux, shallowEqual);

  const { openModal: isTeamMemberSelectorOpen } = useSelector((state) => state.chatListContactsRedux);

  // AI Chat unread count
  const aiUnreadCount = useSelector(selectAIUnreadCount);

  // Streaming state for AI response notification
  const isStreaming = useSelector((state) => state.aiChatListRedux.isStreaming);
  const activeChatType = useSelector((state) => state.supportChatListRedux.activeChatType);
  const lastAIBotMessage = useSelector((state) => {
    const items = state.aiChatListRedux.items || [];
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      if (item.sender === 'bot' && !item.isStreaming && item.message) {
        return item.message;
      }
    }
    return null;
  });
  const prevNotifStreamingRef = useRef(isStreaming);

  // Memoize ordered chats to avoid re-sorting on every render
  const orderedChats = useMemo(() => {
    if (!chats || chats.length === 0) return [];
    
    return [...chats].sort((a, b) => {
      const aMessage = a?.lastMessage;
      const bMessage = b?.lastMessage;

      if (!aMessage && !bMessage) return 0;
      if (!aMessage) return 1;
      if (!bMessage) return -1;

      const aTime = moment(aMessage.createdOn).unix();
      const bTime = moment(bMessage.createdOn).unix();

      if (aTime === bTime) return 0;
      return bTime > aTime ? 1 : -1;
    });
  }, [chats]);

  // Memoize unread chats calculation
  const unreadChats = useMemo(() => {
    if (!chats || !authId) return [];
    return chats.filter(
      (chat) =>
        chat.lastMessage?.status === "Received" &&
        chat.lastMessage?.receiverEntityNameId === authId
    );
  }, [chats, authId]);

  // Combined total unread count (team chat + AI chat)
  const totalUnreadCount = useMemo(() => {
    const teamUnread = 0;
    const aiUnread = aiUnreadCount || 0;
    return teamUnread + aiUnread;
  }, [unreadChats, aiUnreadCount]);

  // Memoize loadChats to prevent recreation
  const loadChats = useCallback((start, query = searchText) => {
    const term = query.trim();
    if (term) {
      setSearchText(term);
    }

    if (start || chats?.length < total) {
      dispatch(
        fetchChats({
          offset: start ? 0 : chats?.length,
          conditions: [{ fields: ["name"], terms: [term] }],
          variableQuery: { userId: JSON.stringify(authId) },
        })
      );
    }
  }, [searchText, chats?.length, total, authId, dispatch]);

  // Memoize event handlers to prevent recreation
  const handleClose = useCallback(() => {
    setSearchText("");
    dispatch(resetChatList());
    dispatch(closeChatList());
    dispatch(closeSupportChat());
    dispatch(closeTeamChatList());
    dispatch(closeTeamMemberSelector());
    dispatch(resetChatThread());
    
    // Clear typing timeout on close
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [dispatch]);

  const handleExpandToggle = useCallback(() => {
    dispatch(setIsExpanded(!isExpanded));
  }, [dispatch, isExpanded]);

  const handleCollapsedExpand = useCallback(() => {
    if (!openModal) {
      dispatch(openChatList({ variableQuery: { userId: JSON.stringify(authId) } }));
    }
    dispatch(setIsExpanded(true));
  }, [openModal, authId, dispatch]);

  // Cleanup on unmount - now with proper dependencies
  useEffect(() => {
    return () => {
      handleClose();
    };
  }, [handleClose]);

  // Fetch AI unread counts when modal opens or authId changes
  useEffect(() => {
    if (openModal && authId) {
      dispatch(fetchAIUnreadCounts({ userId: authId }));
    }
  }, [openModal, authId, dispatch]);

  /**
   * Show a toast notification when the AI response completes while the user
   * is not watching AI chat (away from AI tab, or modal collapsed/closed).
   * Mirrors the team chat NEW_MESSAGE notification pattern.
   */
  useEffect(() => {
    const prevStreaming = prevNotifStreamingRef.current;
    prevNotifStreamingRef.current = isStreaming;

    const justFinished = prevStreaming === true && isStreaming === false;
    // "Away" = not on AI tab, OR chat is collapsed (user can't see the response)
    const userIsAway = activeChatType !== 'ai' || !isExpanded;

    if (justFinished && userIsAway) {
      const notificationBody = lastAIBotMessage || messages['ai_defaultMessage'];
      setAiNotif({
        message: notificationBody,
        title: messages['ai_title'] || 'Neurion AI',
      });

      // When collapsed, ChatsList is unmounted so its increment effects don't fire.
      // Handle unread count update here to keep the collapsed bar badge in sync.
      if (!isExpanded) {
        dispatch(incrementUnreadCount(1));
        if (authId) dispatch(fetchAIUnreadCounts({ userId: authId }));
      }
    }
  }, [isStreaming, activeChatType, isExpanded, lastAIBotMessage, authId, dispatch, messages, openModal]);

  const renderCollapsedDrawer = () => (
    <Grid
      container
      className="chat-collapsed-bar"
      style={{
        position: "fixed",
        bottom: 0,
        right: "1.5rem",
        width: "351px",
        height: "35px",
        zIndex: 1300,
        padding: "0 20px",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Grid style={{ width: "28px" }}>
        <Badge
          sx={{
            "& .MuiBadge-badge": {
              minWidth: "24px",
              height: "24px",
              fontSize: "14px",
              borderRadius: "50%",
            },
          }}
          badgeContent={totalUnreadCount}
          color="primary"
        />
      </Grid>

      <Grid style={{ display: "flex", flexGrow: 1 }}>
        <span className="modal-title">{messages["chat_modalTitle"]}</span>
      </Grid>

      <Grid>
        <Button type="icon" icon="expand_less" className="btn-close" onClick={handleCollapsedExpand} />
      </Grid>

      <Grid>
        <Button type="icon" icon="close" className="btn-close" onClick={handleClose} />
      </Grid>
    </Grid>
  );

  const renderExpandedDrawer = () => {
    const modalTitle = isTeamMemberSelectorOpen
      ? messages["chat_newChat"]
      : `${messages["chat_modalTitle"]} ${totalUnreadCount > 0 ? `(${totalUnreadCount})` : ""}`;

    const showListLoading = loading && !isTeamMemberSelectorOpen;

    return (
      <Modal
        title={modalTitle}
        open={openModal && isExpanded}
        size="small"
        handleClose={handleClose}
        position="right"
        useExpandMore
        handleExpand={handleExpandToggle}
        className="chat-container"
        iconBackClose={isTeamMemberSelectorOpen}
        handleBack={() => dispatch(closeTeamMemberSelector())}
      >
        <ModalBody className="chat-container-body" style={{ overflow: "hidden" }}>
          {isTeamMemberSelectorOpen ? (
            <TeamMemberSelector
              onClose={(item) => {
                if (item) {
                  dispatch(resetChatList({ chats: [] }));
                  loadChats(true);
                  dispatch(openChatThread({ chatId: item.id, authId }));
                }
              }}
            />
          ) : (
            <>
              {showListLoading && <LinearProgress color="primary" className="chat-loading" />}
              <Grid className="chat-toolbar">
                <Grid item className="chat-toolbar-search">
                  <InputSearch
                    id="search-chat"
                    searchIconStyles={{
                      color: "#A2AAAF"
                    }}
                    inputStyles={{
                      height: "unset"
                    }}
                    searchText={searchText}
                    onSearch={(value) => {
                      setSearchText(value);

                      if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                      }

                      typingTimeoutRef.current = setTimeout(() => {
                        dispatch(resetChatList({ chats: [] }));
                        loadChats(true, value);
                      }, 500);
                    }}
                    placeholder={messages["search_user"]}
                  />
                </Grid>
                <Grid item container className="chat-toolbar-button-add"
                  onClick={(e) => {
                    if (e) e?.preventDefault();
                    dispatch(chatListContactsOpen());
                  }}
                >
                  <Add className="chat-toolbar-button-add-icon" />
                  <Typography
                    className="chat-toolbar-button-add-text"
                    variant="body2"
                  >
                    {messages["chat_newChat"]}
                  </Typography>
                </Grid>
              </Grid>
              <Divider/>
              <ChatsList
                authId={authId}
                chatUsers={chatUsers}
                locale={locale}
                messages={messages}
              >
                <TeamChatsList
                  orderFilterData={orderedChats}
                  total={total}
                  getListData={(data) => loadChats(data)}
                  authId={authId}
                  chatUsers={chatUsers}
                  messages={messages}
                  locale={locale}
                  isLoading={isChatsLoading}
                />
              </ChatsList>
            </>
          )}
        </ModalBody>
      </Modal>
    );
  };

  const shouldShowExpanded = openModal && isExpanded;
  const shouldShowCollapsed = openModal && !isExpanded;

  const handleAINotifClick = useCallback(() => {
    if (!openModal) {
      // Drawer fully closed → open it (fetches chat list) then navigate to AI tab
      dispatch(openChatList({ variableQuery: { userId: JSON.stringify(authId) } }));
    } else if (!isExpanded) {
      // Drawer collapsed → expand it first
      dispatch(setIsExpanded(true));
    }
    dispatch(openChat({ chatId: 'ai' }));
  }, [openModal, isExpanded, authId, dispatch]);

  return (
    <>
      {shouldShowExpanded && renderExpandedDrawer()}
      {shouldShowCollapsed && renderCollapsedDrawer()}
      {aiNotif && (
        <AINotification
          title={aiNotif.title}
          message={aiNotif.message}
          duration={10000}
          onClick={handleAINotifClick}
          onClose={() => setAiNotif(null)}
        />
      )}
    </>
  );
};

export default ChatsContainer;
